import React, { useState, useEffect, useMemo } from 'react';
import { DownloadCloud } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';
import { usePermission } from '../context/PermissionContext';
import UniversalList from '../components/UniversalList';

const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) { localStorage.removeItem('token'); window.location.href = '/login'; throw new Error('Session expired'); }
  return res.json();
};

const PAGE_SIZE_DEFAULT = 20;

const CurrencyExchangeRatesPage = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const { hasPermission } = usePermission();
  const canEdit = hasPermission('settings_currency_exchange_rates', 'edit');

  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);

  const loadRates = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/settings/currency-exchange-rates');
      if (res.status === 'success') {
        setRates(res.data || []);
      }
    } catch (e) {
      notify('Failed to load exchange rates', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRates(); }, []);

  // Reset page on search
  useEffect(() => { setPage(1); }, [searchTerm]);

  // Client-side filter
  const filtered = useMemo(() => {
    if (!searchTerm) return rates;
    const q = searchTerm.toLowerCase();
    return rates.filter(r =>
      (r.base_currency || '').toLowerCase().includes(q) ||
      (r.target_currency || '').toLowerCase().includes(q)
    );
  }, [rates, searchTerm]);

  // Client-side paginate
  const totalItems = filtered.length;
  const pageData = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const columns = [
    {
      key: 'base_currency',
      label: t('settings.base_currency'),
      className: 'font-medium',
    },
    {
      key: 'target_currency',
      label: t('settings.target_currency'),
    },
    {
      key: 'rate',
      label: t('settings.exchange_rate'),
      headerClassName: 'text-right',
      className: 'text-right font-mono',
      render: (val) => (parseFloat(val) || 0).toFixed(6),
    },
    {
      key: 'updated_at',
      label: t('common.updated_at'),
      headerClassName: 'text-right',
      className: 'text-right text-slate-500',
      render: (val) => val ? val.substring(0, 10) : '-',
    },
  ];

  return (
    <div className="space-y-6 p-6">
      {/* Title */}
      <div>
        <h1 className="text-2xl font-bold">
          {t('settings.currency_exchange_rates')}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
          {t('settings.currency_exchange_rates_desc')}
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
        <div className="md:hidden" />
        <div className="flex flex-wrap gap-3 md:justify-end">
          {canEdit && (
            <button
              onClick={async () => {
                setSyncing(true);
                try {
                  const res = await apiFetch('/api/settings/currency-exchange-rates/fetch', { method: 'POST', body: '{}' });
                  if (res.status === 'success') {
                    notify(res.message, 'success');
                    loadRates();
                  } else {
                    notify(res.message || 'Sync failed', 'error');
                  }
                } catch (e) {
                  notify('Sync error: ' + e.message, 'error');
                } finally {
                  setSyncing(false);
                }
              }}
              disabled={syncing}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <DownloadCloud size={16} className={syncing ? 'animate-bounce' : ''} />
              {syncing ? t('common.syncing') : t('settings.sync_rates')}
            </button>
          )}
        </div>
      </div>

      <UniversalList
        searchPlaceholder={t('common.search') + '...'}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        columns={columns}
        data={pageData}
        loading={loading}
        emptyMessage={t('common.no_data')}
        rowKey="rate_id"
        page={page}
        totalItems={totalItems}
        pageSize={pageSize}
        setPage={setPage}
        setPageSize={setPageSize}
      />
    </div>
  );
};

export default CurrencyExchangeRatesPage;
